/**
 * Liquid-glass tokens for the **Consultations** admin route only.
 *
 * - Use from `Consultations*Modal.jsx`, `ConsultationsMeetingUpdate.jsx`, and
 *   `ProfileDetails` when `consultationsGlass` is true.
 * - Do **not** import this from shared legacy modals (`OfferDate.jsx`, `MeetingUpdate.jsx`, etc.)
 *   so other admin screens keep their original MDB / MUI v4 styling.
 *
 * `glassDialogPaperProps` / `glassDialogBackdropProps` — spread onto MUI `<Dialog>`.
 */

/** Outer dialog paper — frosted panel */
export const GLASS_DIALOG_PAPER_SX = {
  borderRadius: "14px",
  border: "1px solid rgba(255, 255, 255, 0.45)",
  boxShadow:
    "0 12px 28px rgba(15, 23, 42, 0.08), inset 0 1px 0 rgba(255,255,255,0.55)",
  background:
    "linear-gradient(180deg, rgba(255,255,255,0.76), rgba(255,255,255,0.62))",
  backdropFilter: "blur(18px) saturate(1.15)",
  WebkitBackdropFilter: "blur(18px) saturate(1.15)",
  overflow: "hidden",
};

export const GLASS_DIALOG_BACKDROP_SX = {
  backgroundColor: "rgba(15, 23, 42, 0.38)",
  backdropFilter: "blur(8px)",
};

/** @param {object} [extraPaperSx] MUI `sx` merged after base */
export function glassDialogPaperProps(extraPaperSx) {
  if (!extraPaperSx) {
    return { elevation: 0, sx: GLASS_DIALOG_PAPER_SX };
  }
  return {
    elevation: 0,
    sx: [GLASS_DIALOG_PAPER_SX, extraPaperSx],
  };
}

/** @param {object} [extraBackdropSx] MUI `sx` merged after base */
export function glassDialogBackdropProps(extraBackdropSx) {
  if (!extraBackdropSx) {
    return { sx: GLASS_DIALOG_BACKDROP_SX };
  }
  return {
    sx: [GLASS_DIALOG_BACKDROP_SX, extraBackdropSx],
  };
}

/** Main content card inside DialogContent (e.g. Consultation Details) */
export const GLASS_PANEL_SX = {
  borderRadius: "18px",
  border: "1px solid rgba(255,255,255,0.5)",
  bgcolor: "rgba(255,255,255,0.42)",
  backdropFilter: "blur(16px)",
  WebkitBackdropFilter: "blur(16px)",
  boxShadow:
    "0 12px 30px rgba(15,23,42,0.10), inset 0 1px 0 rgba(255,255,255,0.55)",
};

/** Nested field rows / grids inside GLASS_PANEL_SX */
export const GLASS_FIELD_SURFACE_SX = {
  borderRadius: "14px",
  border: "1px solid rgba(255,255,255,0.45)",
  bgcolor: "rgba(255,255,255,0.38)",
  backdropFilter: "blur(10px)",
  WebkitBackdropFilter: "blur(10px)",
};

/** Default centered modal title (indigo) */
export const GLASS_DIALOG_TITLE_SX = {
  fontWeight: 700,
  fontSize: "1.2rem",
  lineHeight: 1.35,
  pb: 1.25,
  pt: 3,
  px: { xs: 2.5, sm: 3.5 },
  color: "rgba(49, 46, 129, 0.96)",
  textAlign: "center",
};

/** Use on `<Dialog sx={...}>` when portaled poppers must paint outside the paper (date pickers). */
export const GLASS_DIALOG_ROOT_SX_PORTAL_FRIENDLY = {
  "& .MuiDialog-container": {
    alignItems: "center",
  },
  "& .MuiDialog-paper": {
    overflow: "visible",
  },
};
